'use client';

/**
 * Lite mode: simplified graphics for low-end machines and remote/VM displays.
 *
 * The preference is a class on <html>, not React state, for two reasons: the
 * CSS in tailwind.css keys off `html.lite-gfx`, and applying it from an inline
 * script before hydration avoids a flash of the full-graphics theme on every
 * page load.
 *
 * Labelled "Fast", not "Lite". It sits directly beside the theme toggle, whose
 * label is "Light" or "Dark", and "Lite" next to "Light" was read as a second
 * theme button -- the two were reported as a duplicate pair that should have
 * been one control. Only the user-facing word changed; the storage key, the
 * `lite-gfx` class, and every CSS rule keep the original name.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

export const LITE_KEY = 'archlyy.liteMode';

/**
 * Runs before React hydrates, so a lite-mode user never sees a frame of the
 * full theme. Rendered by the root layout; kept tiny and dependency-free
 * because it is inlined into the HTML.
 */
export function LiteModeScript() {
  const script = `try{if(localStorage.getItem('${LITE_KEY}')==='1'){document.documentElement.classList.add('lite-gfx')}}catch(e){}`;
  return <script dangerouslySetInnerHTML={{ __html: script }} />;
}

export function LiteModeToggle() {
  const [lite, setLite] = useState(false);

  // Read the class the inline script already applied, rather than localStorage
  // again -- one source of truth, and it stays correct if the script is
  // blocked.
  useEffect(() => {
    setLite(document.documentElement.classList.contains('lite-gfx'));
  }, []);

  const toggle = useCallback(() => {
    const next = !document.documentElement.classList.contains('lite-gfx');
    document.documentElement.classList.toggle('lite-gfx', next);
    try {
      localStorage.setItem(LITE_KEY, next ? '1' : '0');
    } catch {
      // Private mode or a full quota: the toggle still works for this session.
    }
    setLite(next);
    toast.info(
      next
        ? 'Fast mode on — simplified graphics for low-end displays. This is not the theme.'
        : 'Fast mode off — full graphics restored.'
    );
  }, []);

  return (
    <button
      onClick={toggle}
      aria-pressed={lite}
      className={`nav-pill flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all duration-150 flex-shrink-0 ${
        lite ? 'nav-pill-on' : ''
      }`}
      style={{ ['--tile-accent' as string]: '#a855f7' }}
      title={
        lite
          ? 'Fast mode is ON — simplified graphics for low-end / VM displays. This is not the light/dark theme. Click for full graphics.'
          : 'Fast mode — simpler graphics for low-end / VM displays. Not the light/dark theme. Click to turn on.'
      }
    >
      {/* A gauge, not sparkles: sparkles is the "AI/generate" glyph everywhere
          else in this app, and it said nothing about graphics quality. */}
      <Icon name="BoltIcon" size={12} />
      Fast
    </button>
  );
}
